import { FRAME_MS, RMS_ACTIVE_THRESHOLD } from '@/shared/audio/isAudioSilent';

export function createLevelMonitor(stream, { shouldCount, onHeard, onUnavailable }) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx || !stream) {
    return { ready: Promise.resolve(false), stop() {} };
  }

  const context = new AudioCtx();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const samples = new Float32Array(analyser.fftSize);
  let heardMs = 0;
  let timer = 0;
  let stopped = false;
  let failures = 0;

  const stop = () => {
    stopped = true;
    window.clearTimeout(timer);
    try {
      source.disconnect();
      analyser.disconnect();
    } catch {
      // already disconnected
    }
    context.close().catch(() => {});
  };

  const giveUp = () => {
    if (stopped) return;
    onUnavailable?.();
    stop();
  };

  const tick = () => {
    if (stopped) return;
    try {
      analyser.getFloatTimeDomainData(samples);
      failures = 0;
    } catch {
      failures += 1;
      if (failures >= 3) {
        giveUp();
        return;
      }
      timer = window.setTimeout(tick, FRAME_MS);
      return;
    }
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i += 1) {
      const sample = samples[i];
      sumSquares += sample * sample;
    }
    const rms = Math.sqrt(sumSquares / samples.length);
    if (shouldCount() && rms >= RMS_ACTIVE_THRESHOLD) {
      heardMs += FRAME_MS;
      onHeard(heardMs);
    }
    timer = window.setTimeout(tick, FRAME_MS);
  };

  const ready = context.resume().then(() => {
    if (stopped || context.state !== 'running') return false;
    timer = window.setTimeout(tick, FRAME_MS);
    return true;
  }).catch(() => false);

  return { ready, stop };
}
